import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [impersonation, setImpersonation] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
      setIsPlatformAdmin(!!data.is_platform_admin);
      setImpersonation(data.impersonation || null);
    } catch {
      setUser(false);
      setTenant(null);
      setIsPlatformAdmin(false);
      setImpersonation(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // inject tenant CSS vars
  useEffect(() => {
    if (tenant) {
      document.documentElement.style.setProperty("--tenant-primary", tenant.primary_color || "#0F172A");
      document.documentElement.style.setProperty("--tenant-secondary", tenant.secondary_color || "#F1F5F9");
      document.documentElement.style.setProperty("--tenant-accent", tenant.accent_color || "#22C55E");
    }
  }, [tenant]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      setTenant(data.tenant);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      setTenant(data.tenant);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(false);
    setTenant(null);
  };

  return (
    <AuthContext.Provider value={{ user, tenant, loading, isPlatformAdmin, impersonation, login, register, logout, refresh, setTenant }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
